<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot } from 'radix-vue'
import type { PropType } from 'vue'

const props = withDefaults(
    defineProps<{
        label?: string
        disabled?: boolean
        required?: boolean
        error?: boolean | null
        errorMessage?: string
        size?: 'medium' | 'large'
    }>(),
    {
        size: 'medium',
    },
)

defineEmits(['update:checked'])

const checked = defineModel('checked', { type: [Boolean, String] as PropType<boolean | 'indeterminate'> })

const computedClass = computed(() => {
    return [
        checked.value ? 'bg-plus-lp-dark-blue' : 'border border-solid border-grey-500 bg-white',
        props.error ? 'border-plus-lp-danger' : '',
    ]
})
</script>

<template>
    <label class="flex flex-row gap-4">
        <CheckboxRoot
            v-model:checked="checked"
            :class="computedClass"
            class="focus-visible:outline-focus group box-border flex size-[24px] shrink-0 appearance-none items-center justify-center rounded text-lp-label-sm transition-colors duration-200 ease-linear hover:shadow-[0_0_0_4px_rgba(0,40,61,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-plus-lp-dark-blue-disabled-bg disabled:shadow-none"
            :disabled="disabled"
        >
            <CheckboxIndicator class="flex size-full items-center justify-center rounded">
                <Transition
                    name="fade"
                    mode="out-in"
                >
                    <svg
                        v-if="checked === 'indeterminate'"
                        class="fill-plus-lp-green-one group-disabled:fill-plus-lp-dark-blue-disabled-text"
                        width="12"
                        height="2"
                        viewBox="0 0 12 2"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M0 1C0 0.447715 0.447715 0 1 0H11C11.5523 0 12 0.447715 12 1C12 1.55228 11.5523 2 11 2H1C0.447715 2 0 1.55228 0 1Z"
                        />
                    </svg>

                    <svg
                        v-else-if="checked"
                        class="fill-plus-lp-green-one group-disabled:fill-plus-lp-dark-blue-disabled-text"
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L5.70711 7.70711C5.31658 8.09763 4.68342 8.09763 4.29289 7.70711L0.292893 3.70711C-0.0976311 3.31658 -0.0976311 2.68342 0.292893 2.29289C0.683417 1.90237 1.31658 1.90237 1.70711 2.29289L5 5.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893Z"
                        />
                    </svg>
                </Transition>
            </CheckboxIndicator>
        </CheckboxRoot>
        <span
            :class="[size === 'large' ? 'lp-paragraph-lg' : 'lp-paragraph-md']"
            class="text-start"
        >
            <span
                v-if="required"
                class="before:pr-0.5 before:text-red-500 before:content-['*']"
            />
            <slot name="label">
                {{ label }}
            </slot>
            <br />
            <span
                v-if="error"
                class="mt-2 text-lp-label-xs text-plus-lp-danger"
            >
                {{ errorMessage }}
            </span>
        </span>
    </label>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity duration-200;
}
.fade-enter-from,
.fade-leave-to {
    @apply opacity-0;
}
.fade-enter-to,
.fade-leave-from {
    @apply opacity-100;
}
</style>
